/**
 * Adapter Registry (Story 4.2)
 *
 * Explicit Record<Platform, IntegrationAdapter> const object — no Map, no side-effect registration.
 * 30 platforms doesn't warrant dynamic registration; type-checked and explicit.
 *
 * ADAPTERS is Partial because adapters are added incrementally in Stories 4.6-4.11, 9.x, 10.x, etc.
 * ADAPTER_META is complete — metadata exists for all 30 platforms for the integration picker UI.
 */
import type { Platform } from "@provenleap/db";
import type { IntegrationAdapter, IntegrationMeta } from "./adapter";

// ============================================================================
// OAuth Grant Scope Classification (Story 31.5, ADR-0004 §Axis 1)
// ============================================================================
// The `OAuthGrantScope` type and the REQUIRED `IntegrationMeta.oauthGrantScope`
// field live in `adapter.ts` (the contract file, alongside IntegrationMeta).
// The per-platform classification VALUES live here on ADAPTER_META —
// required-not-optional is the exhaustiveness mechanism: because
// `ADAPTER_META: Record<Platform, IntegrationMeta>`, a platform added without
// a classification is a COMPILE ERROR, not a silent gap. The four documented
// deviations from the ADR-0004 table (google-pagespeed, microsoft-ads,
// pinterest-ads, snapchat-ads) carry inline rationale comments on their
// entries below.

// ============================================================================
// Adapter Registry (populated as adapters are built in later stories)
// ============================================================================

/**
 * Internal registry of implemented integration adapters.
 * Partial because adapters are added incrementally across epics.
 *
 * @internal Prefer registerAdapter() for writes and getAdapter() for reads.
 * Direct mutation is discouraged outside of adapter module initialization.
 */
export const ADAPTERS: Partial<Record<Platform, IntegrationAdapter>> = {};

/**
 * Register an adapter for a platform.
 * Preferred over directly mutating ADAPTERS.
 *
 * @example
 * registerAdapter('google-analytics-4', googleAnalytics4Adapter);
 */
export function registerAdapter(platform: Platform, adapter: IntegrationAdapter): void {
  ADAPTERS[platform] = adapter;
}

// ============================================================================
// Platform Metadata (complete for all 30 platforms)
// ============================================================================

/**
 * Display-level metadata for all supported platforms.
 * Available immediately for integration picker UI, even before adapters are implemented.
 */
export const ADAPTER_META: Record<Platform, IntegrationMeta> = {
  // Google (Epic 4)
  "google-analytics-4": {
    displayName: "Google Analytics 4",
    category: "analytics",
    description: "Website traffic, user behavior, and conversion analytics",
    platform: "google-analytics-4",
    oauthFamily: "google",
    oauthGrantScope: "multi-tenant",
  },
  "google-search-console": {
    displayName: "Google Search Console",
    category: "seo",
    description: "Search performance, indexing status, and keyword rankings",
    platform: "google-search-console",
    oauthFamily: "google",
    oauthGrantScope: "multi-tenant",
  },
  "google-ads": {
    displayName: "Google Ads",
    category: "ads",
    description: "Ad campaigns, spend, conversions, and ROAS metrics",
    platform: "google-ads",
    oauthFamily: "google",
    oauthGrantScope: "multi-tenant",
  },
  "google-business-profile": {
    displayName: "Google Business Profile",
    category: "analytics",
    description: "Local business visibility, reviews, and search impressions",
    platform: "google-business-profile",
    oauthFamily: "google",
    oauthGrantScope: "multi-tenant",
  },
  "youtube-analytics": {
    displayName: "YouTube Analytics",
    category: "social",
    description: "Video performance, audience engagement, and channel growth",
    platform: "youtube-analytics",
    oauthFamily: "google",
    oauthGrantScope: "multi-tenant",
  },
  // google-pagespeed intentionally has NO oauthFamily — it uses authType: "api-key"
  // (see pagespeedAdapter at packages/integrations/src/google/google-pagespeed.ts).
  // The build-time test in oauth-families.test.ts excludes api-key adapters.
  "google-pagespeed": {
    displayName: "Google PageSpeed",
    category: "analytics",
    description: "Core Web Vitals, performance scores, and optimization insights",
    platform: "google-pagespeed",
    // Story 31.5 DEVIATION 1 from ADR-0004's per-platform table: the ADR's
    // coarse "Google (GA4, Ads, GSC, YT, GBP, Pagespeed) = multi-tenant" row
    // is imprecise here — PageSpeed is authType: "api-key" (no OAuth grant,
    // no catalog; it analyzes arbitrary URLs), so it is single-tenant by
    // definition: one key = one account.
    oauthGrantScope: "single-tenant",
  },
  // Social Media (Epics 9, 10, 11)
  "facebook-pages": {
    displayName: "Facebook Pages",
    category: "social",
    description: "Page reach, engagement, follower growth, and post performance",
    platform: "facebook-pages",
    oauthFamily: "meta",
    oauthGrantScope: "multi-tenant",
  },
  "facebook-ads": {
    displayName: "Facebook Ads",
    category: "ads",
    description: "Ad campaigns, audience targeting, spend, and conversions",
    platform: "facebook-ads",
    oauthFamily: "meta",
    oauthGrantScope: "multi-tenant",
  },
  "instagram-business": {
    displayName: "Instagram Business",
    category: "social",
    description: "Post reach, story views, follower demographics, and engagement",
    platform: "instagram-business",
    oauthFamily: "meta",
    oauthGrantScope: "multi-tenant",
  },
  "instagram-ads": {
    displayName: "Instagram Ads",
    category: "ads",
    description: "Instagram ad campaigns, creative performance, and conversions",
    platform: "instagram-ads",
    oauthFamily: "meta",
    // Instagram Ads = Meta Ads with a platform filter (mapping-directive
    // row 10) → multi-tenant with the Meta family.
    oauthGrantScope: "multi-tenant",
  },
  "linkedin-pages": {
    displayName: "LinkedIn Pages",
    category: "social",
    description: "Company page analytics, follower growth, and post engagement",
    platform: "linkedin-pages",
    oauthFamily: "linkedin",
    oauthGrantScope: "multi-tenant",
  },
  "linkedin-ads": {
    displayName: "LinkedIn Ads",
    category: "ads",
    description: "LinkedIn ad campaigns, lead gen forms, and B2B conversions",
    platform: "linkedin-ads",
    oauthFamily: "linkedin",
    oauthGrantScope: "multi-tenant",
  },
  "tiktok-business": {
    displayName: "TikTok Business",
    category: "social",
    description: "Video performance, audience analytics, and ad campaign metrics",
    platform: "tiktok-business",
    oauthFamily: "tiktok",
    oauthGrantScope: "multi-tenant",
  },
  // Advertising (Epic 19)
  "microsoft-ads": {
    displayName: "Microsoft Ads",
    category: "ads",
    description: "Bing search campaigns, spend, conversions, and CPA metrics",
    platform: "microsoft-ads",
    oauthFamily: "microsoft-ads",
    // Story 31.5 DEVIATION 2 from ADR-0004's per-platform table: Microsoft
    // Ads is multi-tenant-CAPABLE (customer→accounts axis) but is treated as
    // single-tenant for V1 (one customer per OAuth grant in our flow), per
    // the explicit demotion decision in story 31-12. The registry records
    // the V1-EFFECTIVE treatment, not the platform's nature.
    // PROMOTION CRITERIA: promote to multi-tenant when 3+ paying customers
    // request multiple Microsoft Ads accounts on this platform.
    // Without explicit triggers, V1-demotions become permanent by inertia.
    oauthGrantScope: "single-tenant",
  },
  "pinterest-ads": {
    displayName: "Pinterest Ads",
    category: "ads",
    description: "Pin promotion campaigns, spend, clicks, and conversions",
    platform: "pinterest-ads",
    oauthFamily: "pinterest-ads",
    // Story 31.5 DEVIATION 2 from ADR-0004's per-platform table: Pinterest
    // Ads is multi-tenant-capable but single-tenant per V1 scope (ad_account_id
    // from /v5/ad_accounts — use first/owned), per the explicit demotion
    // decision in story 31-13. The registry records the V1-EFFECTIVE treatment.
    // PROMOTION CRITERIA: promote to multi-tenant when 3+ paying customers
    // request multiple Pinterest Ads accounts on this platform.
    oauthGrantScope: "single-tenant",
  },
  "snapchat-ads": {
    displayName: "Snapchat Ads",
    category: "ads",
    description: "Snap ad campaigns, swipe-ups, video views, and conversions",
    platform: "snapchat-ads",
    oauthFamily: "snapchat-ads",
    // Story 31.5 DEVIATION 2 from ADR-0004's per-platform table: Snapchat
    // Ads is multi-tenant-capable but single-tenant per V1 scope
    // (organization_id from /v1/me/organizations), per the explicit demotion
    // decision in story 31-14. The registry records the V1-EFFECTIVE treatment.
    // PROMOTION CRITERIA: promote to multi-tenant when 3+ paying customers
    // request multiple Snapchat Ads accounts on this platform.
    oauthGrantScope: "single-tenant",
  },
  "reddit-ads": {
    displayName: "Reddit Ads",
    category: "ads",
    description: "Reddit ad campaigns, impressions, clicks, and conversions",
    platform: "reddit-ads",
    oauthFamily: "reddit-ads",
    oauthGrantScope: "multi-tenant",
  },
  "amazon-ads": {
    displayName: "Amazon Ads",
    category: "ads",
    description: "Amazon PPC campaigns, ACOS, ROAS, sales, and orders",
    platform: "amazon-ads",
    oauthFamily: "amazon-ads",
    // Multi-region profiles (Story 19.13 NA/EU/FE partitioning) are RESOURCES
    // within one LWA grant → multi-tenant, no special casing.
    oauthGrantScope: "multi-tenant",
  },
  // SEO (Epic 20)
  ahrefs: {
    displayName: "Ahrefs",
    category: "seo",
    description: "Domain rating, backlinks, organic traffic, and keyword rankings",
    platform: "ahrefs",
    oauthFamily: "ahrefs",
    // api-key auth: one key = one account, single-tenant by definition.
    oauthGrantScope: "single-tenant",
  },
  semrush: {
    displayName: "Semrush",
    category: "seo",
    description: "Authority score, organic traffic, keywords, and backlink analysis",
    platform: "semrush",
    oauthFamily: "semrush",
    // api-key auth: one key = one account, single-tenant by definition.
    oauthGrantScope: "single-tenant",
  },
  moz: {
    displayName: "Moz",
    category: "seo",
    description: "Domain authority, page authority, spam score, and link metrics",
    platform: "moz",
    oauthFamily: "moz",
    // api-key auth: one key = one account, single-tenant by definition.
    oauthGrantScope: "single-tenant",
  },
  // Email & Commerce (Epic 12)
  mailchimp: {
    displayName: "Mailchimp",
    category: "email",
    description: "Email campaigns, open rates, click rates, and subscriber growth",
    platform: "mailchimp",
    oauthFamily: "mailchimp",
    oauthGrantScope: "single-tenant",
  },
  klaviyo: {
    displayName: "Klaviyo",
    category: "email",
    description: "Email, SMS, and push marketing campaigns with revenue attribution",
    platform: "klaviyo",
    oauthFamily: "klaviyo",
    oauthGrantScope: "single-tenant",
  },
  shopify: {
    displayName: "Shopify",
    category: "ecommerce",
    description: "Store revenue, orders, product performance, and conversion rates",
    platform: "shopify",
    oauthFamily: "shopify",
    // One grant = one store: single-tenant per ADR-0004.
    oauthGrantScope: "single-tenant",
  },
  // Call Tracking (Epic 22)
  callrail: {
    displayName: "CallRail",
    category: "other",
    description: "Call tracking, call attribution, and phone lead analytics",
    platform: "callrail",
    oauthFamily: "callrail",
    // api-key auth: one key = one account, single-tenant by definition.
    oauthGrantScope: "single-tenant",
  },
  calltrackingmetrics: {
    displayName: "CallTrackingMetrics",
    category: "other",
    description: "Call tracking, form submissions, and attribution analytics",
    platform: "calltrackingmetrics",
    oauthFamily: "calltrackingmetrics",
    // api-key auth: one key = one account, single-tenant by definition.
    oauthGrantScope: "single-tenant",
  },
  // Reviews (Epic 23)
  "google-reviews": {
    displayName: "Google Reviews",
    category: "other",
    description: "Google Business Profile reviews, ratings, and reputation metrics",
    platform: "google-reviews",
    // Story 23.1 — SIXTH Google-family platform. Shares Google OAuth with
    // GA4, Search Console, Google Ads, YouTube, and Google Business Profile.
    // No new env keys, no new OAUTH_FAMILIES entry, no new queue handlers.
    // Failure to declare this silently re-creates the 4-broken-families
    // pre-refactor incident from oauth-integration-directive.md.
    oauthFamily: "google",
    // Shares Google Business Profile's grant → multi-tenant with the
    // Google family (one grant exposes the GBP location catalog).
    oauthGrantScope: "multi-tenant",
  },
  trustpilot: {
    displayName: "Trustpilot",
    // Story 23.3 Task 8.1 — opens the Reviews category alongside Google
    // Reviews (also category "other" today; future restructure migrates
    // both to a typed "Reviews" category once the picker UI consumes
    // category strings). Keeping "other" matches Google Reviews precedent
    // until the IntegrationCategory union grows a "reviews" literal.
    category: "other",
    description: "TrustScore, review volume, rating distribution, and reputation",
    platform: "trustpilot",
    // Story 23.3 — SIXTH per-user-supplied API-key family. OAuth-2
    // client_credentials grant: API Key + API Secret pair exchanges for
    // 100-hour Bearer access token; NO refresh token (must re-issue via cron
    // proactive sweep ~24h before expiry). Five divergences from CallRail
    // 22.1: TWO secrets in dialog + finite TTL + REAL recoveryRefresh + NO
    // retention pre-flight + domain-find discovery.
    oauthFamily: "trustpilot",
    // api-key auth (client_credentials key+secret): one credential pair =
    // one account, single-tenant by definition.
    oauthGrantScope: "single-tenant",
  },
  yelp: {
    displayName: "Yelp",
    // Story 23.5 Task 10.1 — keeps "other" category to match Google Reviews /
    // Trustpilot precedent until the IntegrationCategory union grows a
    // "reviews" literal. Reviews-category integrations all carry "other"
    // today; future restructure migrates them en masse.
    category: "other",
    description: "Yelp reviews, ratings, and local business reputation metrics",
    platform: "yelp",
    // Story 23.5 — SEVENTH per-user-supplied API-key family. Pure Bearer
    // API-Key auth (NO OAuth handshake, NO token exchange, NO refresh).
    // SIX divergences from Trustpilot 23.3: ONE secret in dialog + permanent
    // TTL + PR-1-stub recoveryRefresh + NO proactive sweep + NEW D1
    // yelp_rating_snapshots + NEW per-tenant daily-call-budget circuit-breaker.
    oauthFamily: "yelp",
    // api-key auth: one key = one account, single-tenant by definition.
    oauthGrantScope: "single-tenant",
  },
  // CRM (Epic 25)
  hubspot: {
    displayName: "HubSpot",
    category: "crm",
    description: "CRM contacts, deals pipeline, lead attribution, and sales metrics",
    platform: "hubspot",
    oauthFamily: "hubspot",
    // One grant = one HubSpot portal: single-tenant per ADR-0004.
    oauthGrantScope: "single-tenant",
  },
};

// ============================================================================
// Registry Lookup Functions
// ============================================================================

/**
 * Get an adapter by platform. Returns undefined if the adapter is not yet implemented.
 */
export function getAdapter(platform: Platform): IntegrationAdapter | undefined {
  return ADAPTERS[platform];
}

/**
 * Get all platforms that have registered (implemented) adapters.
 */
export function getRegisteredPlatforms(): Platform[] {
  return Object.keys(ADAPTERS) as Platform[];
}

/**
 * Get display metadata for all supported platforms.
 * Returns all 30 platforms regardless of adapter implementation status.
 */
export function getAllPlatformMeta(): IntegrationMeta[] {
  return Object.values(ADAPTER_META);
}

/**
 * Get display metadata for a specific platform.
 */
export function getPlatformMeta(platform: Platform): IntegrationMeta {
  return ADAPTER_META[platform];
}

/**
 * Per-platform favicon domain → Google S2 favicon URL.
 *
 * LEGACY / CURRENTLY UNUSED at runtime. Integration logos are now served from
 * locally-vendored assets via the web `logoUrl` / CUSTOM_LOGOS map
 * (apps/web/src/lib/integrations.ts) — that is the real source of truth. The PDF
 * export renders the same web print route (headless browser), so editor and PDF
 * already share those local icons; `platformLogoUrl` below has no live caller
 * (only a barrel re-export). Kept for now; if a future server-side use appears,
 * vendor those icons too rather than reintroducing the external dependency (and
 * note the 5 hand-curated web icons differ from Google's generic favicons).
 */
export const PLATFORM_LOGO_DOMAINS: Record<string, string> = {
  "google-analytics-4": "analytics.google.com",
  "google-search-console": "search.google.com",
  "google-ads": "ads.google.com",
  "google-business-profile": "business.google.com",
  "youtube-analytics": "youtube.com",
  "google-pagespeed": "pagespeed.web.dev",
  "facebook-pages": "facebook.com",
  "facebook-ads": "facebook.com",
  "instagram-business": "instagram.com",
  "instagram-ads": "instagram.com",
  "linkedin-pages": "linkedin.com",
  "linkedin-ads": "linkedin.com",
  "tiktok-business": "tiktok.com",
  mailchimp: "mailchimp.com",
  klaviyo: "klaviyo.com",
  shopify: "shopify.com",
  trustpilot: "trustpilot.com",
  yelp: "yelp.com",
  ahrefs: "ahrefs.com",
  semrush: "semrush.com",
  moz: "moz.com",
  callrail: "callrail.com",
  "calltrackingmetrics": "calltrackingmetrics.com",
  "amazon-ads": "advertising.amazon.com",
  "microsoft-ads": "ads.microsoft.com",
  "pinterest-ads": "pinterest.com",
  "snapchat-ads": "snapchat.com",
  "x-ads": "x.com",
  "reddit-ads": "reddit.com",
  hubspot: "hubspot.com",
};

/** Favicon logo URL for a platform (Google favicon service), or null if unknown. */
export function platformLogoUrl(platform: string | undefined): string | null {
  if (!platform) return null;
  const domain = PLATFORM_LOGO_DOMAINS[platform];
  return domain ? `https://www.google.com/s2/favicons?domain=${domain}&sz=64` : null;
}

/** Human display name for a platform (from ADAPTER_META), or the raw id if unknown. */
export function platformDisplayName(platform: string | undefined): string {
  if (!platform) return "";
  return ADAPTER_META[platform as Platform]?.displayName ?? platform;
}
